// Persistent preview cache (IndexedDB). mediaCache (in-memory blob URLs) is
// session-lived, so every reload re-downloads every thumbnail — and a reload
// is exactly the recovery path when the media-DC sender dies. This layer
// keeps preview JPEG blobs across reloads, keyed identically to mediaCache
// ("<chat>:<msgId>" / "dm:<msgId>" — ids and bytes of the user's OWN media,
// on their own device; the server still stores no content). Best-effort:
// every operation silently no-ops when IndexedDB is unavailable (bun tests,
// some private-browsing modes). Cleared on logout.

const DB_NAME = "snail-media";
const STORE = "previews";
export const MAX_ENTRIES = 300;
export const PRUNE_TO = 250;

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  if (!dbPromise) {
    dbPromise = new Promise((resolve) => {
      try {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
          const store = req.result.createObjectStore(STORE, { keyPath: "key" });
          store.createIndex("ts", "ts");
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }
  return dbPromise;
}

/** How many oldest entries to evict at `total` stored entries: none until
 *  the cap, then down to PRUNE_TO in one sweep (hysteresis, so we don't
 *  prune on every put). */
export function pruneCount(total: number): number {
  return total > MAX_ENTRIES ? total - PRUNE_TO : 0;
}

export async function getPreview(key: string): Promise<Blob | undefined> {
  const db = await openDb();
  if (!db) return undefined;
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE).objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result?.blob);
      req.onerror = () => resolve(undefined);
    } catch {
      resolve(undefined);
    }
  });
}

export function putPreview(key: string, blob: Blob): void {
  void (async () => {
    const db = await openDb();
    if (!db) return;
    try {
      const store = db.transaction(STORE, "readwrite").objectStore(STORE);
      store.put({ key, blob, ts: Date.now() });
      const countReq = store.count();
      countReq.onsuccess = () => {
        let toDrop = pruneCount(countReq.result);
        if (!toDrop) return;
        const cursorReq = store.index("ts").openCursor();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor || toDrop <= 0) return;
          toDrop--;
          cursor.delete();
          cursor.continue();
        };
      };
    } catch {
      /* best-effort */
    }
  })();
}

export async function clearPreviews(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const req = db.transaction(STORE, "readwrite").objectStore(STORE).clear();
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}
